import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, Inject, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { State, states } from './basic-info-const';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule, MatSnackBar, MAT_SNACK_BAR_DATA } from '@angular/material/snack-bar';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { phoneValidator, requiredEmailValidator, zipCodeValidator } from '@k12/validators';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'error-snackbar',
  imports: [MatButtonModule],
  template: `
    <div class="error-snackbar">
      <span>{{ data.message }}</span>
      <button mat-button (click)="snackBarRef.dismiss()">Close</button>
    </div>
  `,
  styles: [
    `
      .error-snackbar {
        white-space: pre-line;
      }
    `
  ]
})
export class ErrorSnackbarComponent {
  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    public snackBarRef: MatSnackBar,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    @Inject(MAT_SNACK_BAR_DATA) public data: { message: string }
  ) {}
}

export type InfoSection = {
	id: string;
	name: string;
	fields: InfoField[];
	description?: string;
	selection?: boolean;
}

type InfoField = {
	id: string;
	addressId?: string;
	name: string;
	type: InfoFieldType;
	value?: SelectionField[] | string | null;
	detail?: string;
	placeholder?: string;
	required?: boolean;
}

type SelectionField = {
	name: string;
	editSelection: boolean;
}

export enum InfoFieldType {
  TEXT = 'text',
  STATE = 'state',
  ZIP = 'zip',
  EMAIL = 'email',
  PHONE = 'phone',
  SELECTION = 'selection',
  PASSWORD = 'password'
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'basic-info',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatSnackBarModule,
    NgxMaskDirective
  ],
  providers: [provideNgxMask()],
  templateUrl: './basic-info.component.html',
  styleUrl: './basic-info.component.scss'
})
export class BasicInfoComponent implements OnInit, OnChanges {
  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    private fb: FormBuilder
  ) {
    this.form = this.fb.group({});
  }

  @Input() sections: InfoSection[];
  @Output() formSubmit = new EventEmitter<Record<string, unknown>>();

  public form: FormGroup;
  public hidePassword: boolean = true;
  public JSON = JSON;

  public readonly usStates = states;

  public readonly infoFieldType = InfoFieldType;

  public readonly errorMessage = 'You are not eligible to provide services if you change your address outside of N.C';

  public showSectionIdStateWarning: string[] = [];
  public showMessage: boolean = false;

  public states: State[] = states;

  private copyInfo: boolean = false;

  private tmpProfile: string = '';

  private snackBar = Inject(MatSnackBar);

  public ngOnInit(): void {
    if (this.sections) {
      this.createForm();
    }

    this.form.get('basicInfo')?.valueChanges.subscribe((value) => {
      if (this.copyInfo) {
        this.form.get('businessInfo')?.patchValue(value, { emitEvent: false });
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['sections'] && changes['sections'].currentValue) {
      this.createForm();
    }
  }

  private createForm(): void {
    if (!this.sections) {
      console.warn('Sections input is required for basic-info component');
      return;
    }

    this.form = this.fb.group({});

    this.sections.forEach((section) => {
      this.form.addControl(section.id, this.fb.group(this.createFields(section)));
    });
  }

  private createFields(section: InfoSection): Record<string, FormControl> {
    const group: Record<string, FormControl> = {};
    const fields = JSON.parse(JSON.stringify(section.fields));
    fields.forEach((field: InfoField) => {
      const validators: Array<(control: AbstractControl) => ValidationErrors | null> = [];
      if (field.required !== false) {
        validators.push(Validators.required);
      }

      if (field.type === InfoFieldType.EMAIL) {
        validators.push(requiredEmailValidator());
      }

      if (field.type === InfoFieldType.PHONE) {
        validators.push(phoneValidator());
      }

      if (field.type === InfoFieldType.ZIP) {
        validators.push(zipCodeValidator());
      }

      let stateValue: { name: string; code: string } | undefined;

      if (field.type === InfoFieldType.STATE) {
        const state: State | undefined = this.states?.find((state) => state.code === field.value);
        stateValue = state ? state : undefined;
      }

      const fieldValue = field.value ? field.value : '';
      const outputValue = stateValue ? stateValue : fieldValue;

      const control = this.fb.control(outputValue, validators.length > 0 ? validators : null);
      group[field.id] = control;
    });
    return group;
  }

  public phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      //Regular expression for phone number
      const phoneRegex = /^\(\d{3}\)\d{3}-\d{4}$/;
      const valid = phoneRegex.test(control.value);
      return valid ? null : { invalidPhoneNumber: true };
    };
  }

  public zipCodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      // Remove any trailing hyphen that might be added by the input mask
      const cleanValue = value ? value.replace(/-$/, '') : value;

      // Regular expression for 5-digit or 9-digit ZIP code formats
      const zipCodePattern = /^(?:\d{5}|\d{5}-\d{4})$/;

      if (cleanValue && !zipCodePattern.test(cleanValue)) {
        return { invalidZipCode: true };
      }
      return null;
    };
  }

  public addProfile(event): void {
    event.value.unshift({ name: 'New Profile', editSelection: false });
  }

  public removeProfile(event, index: number): void {
    if (index > -1) {
      event.value.splice(index, 1);
    }
  }

  public editProfile(event, index: number): void {
    event.value[index].editSelection = true;
    this.tmpProfile = event.value[index].name;
  }

  public saveProfile(event, index: number): void {
    event.value[index].editSelection = false;
  }

  public cancelProfile(event, index: number): void {
    event.value[index].editSelection = false;
    event.value[index].name = this.tmpProfile;
  }

  public toggleCopyBasicInfo(event): void {
    const checkbox = event.target as HTMLInputElement;
    this.copyInfo = checkbox.checked;
    this.copyBasicInfo();
  }

  public copyBasicInfo(): void {
    if (this.copyInfo) {
      const basicInfo = this.form.get('basicInfo')?.value;
      this.form.get('businessInfo')?.patchValue(basicInfo);
    } else if (this.sections.length < 2 || this.sections[1].id === undefined) {
      const businessInfoGroup = this.form.get('businessInfo');
      businessInfoGroup?.reset();
    }
  }

  public disableField(sectionId: string, fieldId: string, mask?: string): boolean {
    if (sectionId !== 'businessInfo') {
      return false;
    }

    const value = this.form.get('basicInfo')?.value[fieldId];

    if (value == '') {
      return false;
    }

    if (mask && value == mask) {
      return false;
    }

    return true;
  }

  public onSubmit(): void {
    if (this.form.valid) {
      // Create a copy of the form value
      const formValue = { ...this.form.value };

      // If business info is disabled but we're copying basic info, ensure business info is included
      if (this.copyInfo && this.form.get('businessInfo')?.disabled) {
        // Get the basic info values
        const basicInfoValues = this.form.get('basicInfo')?.value;

        // Create a new business info object with the basic info values
        formValue.businessInfo = { ...basicInfoValues };
      }

      this.formSubmit.emit(formValue);
    } else {
      const invalidFields: string[] = [];

      Object.keys(this.form.controls).forEach((sectionKey) => {
        const sectionGroup = this.form.get(sectionKey) as FormGroup;
        if (sectionGroup) {
          Object.keys(sectionGroup.controls).forEach((fieldKey) => {
            const control = sectionGroup.get(fieldKey);
            if (control && control.invalid) {
              control.markAsTouched();
              const section = this.sections.find((s) => s.id === sectionKey);
              const field = section?.fields.find((f) => f.id === fieldKey);
              if (field) {
                invalidFields.push(field.name);
              }
            }
          });
        }
      });

      const message =
        invalidFields.length > 0
          ? `Please fill out all required fields:\n${invalidFields.join('\n')}\n`
          : 'Please fill out all required fields';

      this.snackBar.openFromComponent(ErrorSnackbarComponent, {
        data: { message },
        duration: 5000,
        panelClass: ['error-snackbar'],
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    }
  }

  public onStateChange(event: { value: { code: string } }, sectionId: string): void {
    const selectedState = event.value.code;
    if (selectedState !== 'NC') {
      this.showSectionIdStateWarning.push(sectionId);
    } else {
      this.showSectionIdStateWarning = this.showSectionIdStateWarning.filter((id) => id !== sectionId);
    }
  }
}
